import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  UserGroupIcon,
  PlusIcon,
  XMarkIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { smsAPI } from '../../../services/api';
import { getApiBaseUrl } from '../../../config/apiBase';
import UniversalSearchInput from '../../../components/common/UniversalSearchInput';

const UserRoles = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [userPermissions, setUserPermissions] = useState(null);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [usersResponse, rolesResponse] = await Promise.all([
        smsAPI.getUsersWithRoles(),
        smsAPI.getRoles(),
      ]);
      setUsers(usersResponse.data || []);
      setRoles(rolesResponse.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      setUsers([]);
      setRoles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignRoles = (user) => {
    setSelectedUser(user);
    setSelectedRoleIds(user.roles?.map(r => r.id) || []);
    setShowModal(true);
  };

  const handleViewPermissions = async (user) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/admin/user-permissions/${user.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      setUserPermissions(data);
      setShowPermissionsModal(true);
    } catch (error) {
      console.error('Failed to fetch user permissions:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await smsAPI.assignRoles({
        user_id: selectedUser.id,
        role_ids: selectedRoleIds,
      });
      setShowModal(false);
      fetchData();
    } catch (error) {
      console.error('Failed to assign roles:', error);
      alert(error.response?.data?.error || 'Failed to assign roles');
    }
  };

  const toggleRole = (roleId) => {
    setSelectedRoleIds(prev =>
      prev.includes(roleId)
        ? prev.filter(id => id !== roleId)
        : [...prev, roleId]
    );
  };

  const filteredUsers = users.filter(user =>
    user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.full_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRoleBadgeColor = (roleCode) => {
    const colors = {
      master_admin: 'bg-purple-100 text-purple-800',
      admin: 'bg-blue-100 text-blue-800',
      user_manager: 'bg-green-100 text-green-800',
      report_viewer: 'bg-yellow-100 text-yellow-800',
      sms_operator: 'bg-pink-100 text-pink-800',
    };
    return colors[roleCode] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4F46E5]"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#111827]">User Role Assignment</h1>
        <p className="text-sm text-[#6B7280] mt-1">
          Assign roles to users and manage their permissions
        </p>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <UniversalSearchInput
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search users by name, username, or email..."
          />
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg border border-[#E5E7EB] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                  Assigned Roles
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E7EB]">
              {filteredUsers.map((user) => (
                <motion.tr
                  key={user.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="hover:bg-[#F9FAFB] transition-colors"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-[#EEF2FF] flex items-center justify-center">
                          <UserGroupIcon className="h-5 w-5 text-[#4F46E5]" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-[#111827]">{user.full_name}</div>
                        <div className="text-sm text-[#6B7280]">@{user.username}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-[#6B7280]">{user.email}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-2">
                      {user.roles && user.roles.length > 0 ? (
                        user.roles.map((role) => (
                          <span
                            key={role.id}
                            className={`px-2 py-1 text-xs font-medium rounded ${getRoleBadgeColor(role.role_code)}`}
                          >
                            {role.role_name}
                          </span>
                        ))
                      ) : (
                        <span className="text-sm text-[#9CA3AF]">No roles assigned</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded ${
                      user.is_active
                        ? 'bg-[#D1FAE5] text-[#065F46]'
                        : 'bg-[#FEE2E2] text-[#991B1B]'
                    }`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => handleAssignRoles(user)}
                        className="text-[#4F46E5] hover:text-[#4338CA] font-medium"
                      >
                        Assign Roles
                      </button>
                      <button
                        onClick={() => handleViewPermissions(user)}
                        className="text-[#059669] hover:text-[#047857] font-medium"
                      >
                        View Permissions
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Assign Roles Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-lg shadow-xl max-w-2xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-[#E5E7EB]">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-[#111827]">
                    Assign Roles to {selectedUser?.full_name}
                  </h2>
                  <button
                    onClick={() => setShowModal(false)}
                    className="p-2 hover:bg-[#F3F4F6] rounded-lg transition-colors"
                  >
                    <XMarkIcon className="w-5 h-5 text-[#6B7280]" />
                  </button>
                </div>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="p-6 space-y-4">
                  <div className="text-sm text-[#6B7280] mb-4">
                    Select one or more roles to assign to this user
                  </div>

                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {roles.map((role) => (
                      <label
                        key={role.id}
                        className="flex items-start gap-3 p-4 border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={selectedRoleIds.includes(role.id)}
                          onChange={() => toggleRole(role.id)}
                          className="mt-1 w-4 h-4 text-[#4F46E5] border-[#D1D5DB] rounded focus:ring-[#4F46E5]"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-[#111827]">{role.role_name}</span>
                            {role.is_system_role && (
                              <span className="px-2 py-0.5 text-xs font-medium bg-[#FEF3C7] text-[#92400E] rounded">
                                System
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-[#6B7280] mt-1">{role.description}</p>
                        </div>
                      </label>
                    ))}
                  </div>

                  <div className="mt-4 p-4 bg-[#EEF2FF] rounded-lg">
                    <p className="text-sm text-[#4F46E5] font-medium">
                      {selectedRoleIds.length} role(s) selected
                    </p>
                  </div>
                </div>

                <div className="p-6 border-t border-[#E5E7EB] flex gap-3 justify-end">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 text-[#374151] bg-white border border-[#D1D5DB] rounded-lg hover:bg-[#F9FAFB] transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-[#4F46E5] text-white rounded-lg hover:bg-[#4338CA] transition-colors flex items-center gap-2"
                  >
                    <CheckIcon className="w-5 h-5" />
                    Assign Roles
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* View Permissions Modal */}
      <AnimatePresence>
        {showPermissionsModal && userPermissions && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowPermissionsModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-[#E5E7EB]">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-[#111827]">User Permissions</h2>
                  <button
                    onClick={() => setShowPermissionsModal(false)}
                    className="p-2 hover:bg-[#F3F4F6] rounded-lg transition-colors"
                  >
                    <XMarkIcon className="w-5 h-5 text-[#6B7280]" />
                  </button>
                </div>
              </div>

              <div className="p-6 overflow-y-auto max-h-[calc(80vh-140px)]">
                <div className="mb-4">
                  <h3 className="font-medium text-[#111827] mb-2">Assigned Roles</h3>
                  <div className="flex flex-wrap gap-2">
                    {userPermissions.roles && userPermissions.roles.length > 0 ? (
                      userPermissions.roles.map((role, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 text-sm font-medium bg-[#EEF2FF] text-[#4F46E5] rounded-lg"
                        >
                          {role}
                        </span>
                      ))
                    ) : (
                      <span className="text-sm text-[#9CA3AF]">No roles assigned</span>
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="font-medium text-[#111827] mb-2">
                    Permissions ({userPermissions.permissions?.length || 0})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {userPermissions.permissions && userPermissions.permissions.length > 0 ? (
                      userPermissions.permissions.map((permission, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-2 p-2 bg-[#F9FAFB] rounded text-sm text-[#374151]"
                        >
                          <CheckIcon className="w-4 h-4 text-[#059669]" />
                          {permission}
                        </div>
                      ))
                    ) : (
                      <span className="text-sm text-[#9CA3AF]">No permissions assigned</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="p-6 border-t border-[#E5E7EB] flex justify-end">
                <button
                  onClick={() => setShowPermissionsModal(false)}
                  className="px-4 py-2 bg-[#4F46E5] text-white rounded-lg hover:bg-[#4338CA] transition-colors"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UserRoles;
